import { useState, FormEvent } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  checkAuth,
  login,
  requestPasswordReset,
  signup,
} from "../../services/api";
import { Layout } from "../Layout";

interface LocationState {
  message: string | null;
}

export enum AuthFormType {
  LOGIN = "login",
  SIGNUP = "signup",
  FORGOT_PASSWORD = "forgot-password",
}

interface AuthFormProps {
  isLogin: boolean;
  formType: AuthFormType;
  initialMsg: string | null;
  isAuthenticated: boolean;
  setIsAuthenticated: (value: boolean) => void;
  setUser: (user: { id: string; email: string }) => void;
}

export function AuthForm({
  isLogin,
  formType,
  isAuthenticated,
  setIsAuthenticated,
  setUser,
}: AuthFormProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const emailInSearchParams = !!searchParams.get("email");
  const [email, setEmail] = useState<string>(searchParams.get("email") ?? "");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  if (isAuthenticated) {
    return <Navigate to="/subscription" />;
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (formType === AuthFormType.LOGIN) {
        const response = await login({ email, password });
        localStorage.setItem("authToken", response.token);
        const { user } = await checkAuth();
        setIsAuthenticated(true);
        setUser(user!);
        navigate("/subscription");
      } else if (formType === AuthFormType.SIGNUP) {
        const { conflict } = await signup({ email });
        if (!conflict) {
          navigate("/verify-prompt", { state: { email } });
        } else {
          navigate(`/login?email=${email}`);
        }
      } else {
        await handleForgotPassword(e);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unexpected error occurred"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    // Validate email
    if (!email) {
      setError("Email is required");
      return;
    }

    try {
      await requestPasswordReset({ email: email.trim() });
      navigate("/reset-password-sent", { state: { email } });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unexpected error occurred"
      );
    }
  };

  return (
    <Layout>
      <div className="row flex-grow-1 justify-content-center">
        <div className="col d-flex" style={{ position: "relative" }}>
          <img
            src="/birdie_logo.svg"
            alt="Company Logo"
            style={{ height: "40px", position: "absolute", top: "0" }}
          />
          <div>
            <div
              className="font-size-large font-weight-bold"
              style={{ marginTop: "120px" }}
            >
              {formType === AuthFormType.LOGIN
                ? "Sign in"
                : formType === AuthFormType.SIGNUP
                ? "Create an account"
                : "Reset your password"}
            </div>
            <div>
              {formType === AuthFormType.LOGIN
                ? "Enter your email and password"
                : formType === AuthFormType.SIGNUP
                ? "Enter your email"
                : "We'll send a reset password link to your email"}
            </div>
          </div>
        </div>
        <div className="col d-flex">
          <form
            className="flex-grow-1 d-flex flex-column justify-content-between"
            onSubmit={handleSubmit}
          >
            <div
              style={{
                marginTop: isLogin
                  ? "calc(120px - 0.5rem)"
                  : "calc(120px + 0.75rem)",
              }}
            >
              <input
                type="email"
                className={`form-control ${error ? "is-invalid" : ""}`}
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                required
              />
              {isLogin && (
                <>
                  <div className="mt-2">
                    <input
                      type="password"
                      className={`form-control ${error ? "is-invalid" : ""}`}
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>
                  <div className="mt-2">
                    <a href="/forgot-password" className="text-decoration-none">
                      Forgot password?
                    </a>
                  </div>
                </>
              )}
            </div>

            {error && (
              <div className="text-center mt-3">
                <div className="alert alert-danger py-2" role="alert">
                  {error}
                </div>
              </div>
            )}

            {message && (
              <div className="text-center mt-3">
                <div className="alert alert-success py-2" role="alert">
                  {message}
                </div>
              </div>
            )}

            <div className="d-flex align-items-center justify-content-end">
              {!emailInSearchParams ? (
                <div className="me-3">
                  <a
                    href={isLogin ? "/signup" : "/login"}
                    className="text-decoration-none"
                  >
                    {formType === AuthFormType.LOGIN
                      ? "Don't have an account?"
                      : formType === AuthFormType.SIGNUP
                      ? "Already have an account?"
                      : "Back"}
                  </a>
                </div>
              ) : null}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : null}
                {isLogin ? "Login" : "Next"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
